import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'
import { ActivatedRoute } from '@angular/router'

import { BAND_UI, formatScore } from '@components/ciec-analysis-view/tabs/risk-scorecard.labels'
import { ApiPreofferService } from '@data/services/modules/preoffer/api-preoffer.service'
import { TRIGGER_LABELS } from '../preoffer-reviews-list/preoffer-reviews-list.component'
import { PreofferDetail, PreofferStatus } from '@data/interfaces/preoffer.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary'

//* Dónde se ve cada señal en la foto: los juicios y las listas en «Legal y corporativo»; la 32-D en «Análisis».
const SIGNAL_SUBTAB: Record<string, 'legal' | 'analysis'> = { lawsuit: 'legal', blacklist: 'legal', tax_compliance: 'analysis' }
const SIGNAL_ICON: Record<string, string> = { lawsuit: 'fa-scale-balanced', blacklist: 'fa-user-shield', tax_compliance: 'fa-file-invoice' }
//* El título de la página dice en qué quedó; la etiqueta lo repite con su color y su ícono (el color nunca solo).
const STATUS_UI: Partial<Record<PreofferStatus, { title: string; label: string; severity: TagSeverity; icon: string }>> = {
  legal_review: { title: 'Preoferta por autorizar', label: 'Por autorizar', severity: 'warning', icon: 'pi pi-clock' },
  issued: { title: 'Preoferta autorizada', label: 'Autorizada', severity: 'success', icon: 'pi pi-check-circle' },
  returned: { title: 'Preoferta regresada', label: 'Regresada', severity: 'danger', icon: 'pi pi-undo' },
}
const NO_LONGER_CURRENT = { title: 'Preoferta', label: 'Ya no vigente', severity: 'secondary' as const, icon: 'pi pi-history' }
const TONE_SEVERITY: Record<string, TagSeverity> = { ok: 'success', warn: 'warning', bad: 'danger' }
const MIN_REASON = 10

/**
 * Detalle de una preoferta en la bandeja de Legal (plan v4.3 §8.7 y §9.3): la evidencia congelada —señales con su
 * conteo, monto, puntaje, banda y lo que se cotizó— y las dos acciones de Legal. Admin lo ve en solo lectura. Si la foto
 * cambió desde que se decidió, se dice antes de autorizar.
 */
@Component({
  selector: 'app-preoffer-review-detail',
  templateUrl: './preoffer-review-detail.component.html',
  styleUrls: ['./preoffer-review-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class PreofferReviewDetailComponent implements OnInit {
  id = ''
  detail: PreofferDetail | null = null
  loading = false
  loadError: string | null = null
  saving = false
  //* Qué decisión está en vuelo: su botón muestra el giro y todos quedan deshabilitados hasta la respuesta.
  deciding: 'authorized' | 'returned' | null = null
  returning = false
  reason = ''

  readonly triggers = TRIGGER_LABELS
  readonly minReason = MIN_REASON
  readonly canReview = inject(AuthService).hasModulePermission('lease-preoffer', 'review')

  private readonly api = inject(ApiPreofferService)
  private readonly cd = inject(ChangeDetectorRef)
  private readonly confirmation = inject(ConfirmationService)
  private readonly messages = inject(MessageService)
  private readonly route = inject(ActivatedRoute)

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? ''
    this.load()
  }

  get isPending(): boolean {
    return this.detail?.status === 'legal_review'
  }

  get editable(): boolean {
    return this.isPending && this.canReview
  }

  /** §8.7 · La foto cambió desde que se pidió la autorización (revisión N → M). */
  get snapshotDrift(): { from: number; to: number } | null {
    const from = Number(this.detail?.inputs?.['viewedRevision'])
    const to = Number(this.detail?.currentSnapshotRevision)
    return from > 0 && to > from ? { from, to } : null
  }

  get statusUi(): { title: string; label: string; severity: TagSeverity; icon: string } | null {
    if (!this.detail) return null
    return STATUS_UI[this.detail.status] ?? NO_LONGER_CURRENT
  }

  get bandUi(): { label: string; severity: TagSeverity } | null {
    const band = this.detail?.scorecard?.band
    const ui = band ? (BAND_UI as Record<string, { label: string; tone: string }>)[band] : null
    return ui ? { label: ui.label, severity: TONE_SEVERITY[ui.tone] ?? 'secondary' } : null
  }

  //* Con el mismo redondeo que la Calculadora del expediente: el número de Legal y el de la calculadora no difieren.
  get scoreText(): string {
    return formatScore(this.detail?.scorecard?.totalScore)
  }

  //* D-75 · Lo que se preaprobó al decidir: plazo y pago de la oferta conservadora con el tope.
  get preapproval(): { termMonths: number | null; monthlyPayment: number | null } | null {
    return this.detail?.offer ?? this.detail?.inputs?.['preapproval'] ?? null
  }

  get reasonValid(): boolean {
    return this.reason.trim().length >= MIN_REASON
  }

  subtabFor(kind: string): string {
    return SIGNAL_SUBTAB[kind] ?? 'legal'
  }

  signalIcon(kind: string): string {
    return SIGNAL_ICON[kind] ?? 'fa-circle-exclamation'
  }

  load(): void {
    this.loading = true
    this.loadError = null
    this.api.detail(this.id).subscribe((response) => {
      this.loading = false
      //* Una decisión recién guardada suelta los botones hasta que llega cómo quedó, no antes: así no hay doble clic.
      this.saving = false
      this.deciding = null
      if (response.error) this.loadError = response.status === 404 ? 'Esa preoferta no existe.' : response.msg
      else this.detail = response.data
      this.cd.markForCheck()
    })
  }

  authorize(): void {
    const detail = this.detail
    if (!detail || !this.editable) return
    const amount = `$${Math.round(Number(detail.expectedAmount) || 0).toLocaleString('es-MX')} MXN`
    const to = detail.clientSummary?.email ? ` a ${detail.clientSummary.email}` : ' al correo del cliente'
    this.confirmation.confirm({
      key: 'preoffer-review',
      header: 'Autorizar y enviar',
      message: `Se enviará${to} la oferta preaprobada por ${amount} (IVA incluido), con la vigencia de la configuración. ¿Autorizar?`,
      acceptLabel: 'Autorizar y enviar',
      rejectLabel: 'Cancelar',
      accept: () => this.decide({ decision: 'authorized' }),
    })
  }

  startReturn(): void {
    this.returning = true
    this.reason = ''
  }

  submitReturn(): void {
    if (!this.reasonValid) return
    this.decide({ decision: 'returned', reason: this.reason.trim() })
  }

  private decide(body: { decision: 'authorized' | 'returned'; reason?: string }): void {
    this.saving = true
    this.deciding = body.decision
    this.cd.markForCheck()
    this.api.review(this.id, body).subscribe((response) => {
      if (response.error) {
        this.saving = false
        this.deciding = null
        //* 409: otra persona decidió primero o la preoferta cambió. Se recarga para ver cómo quedó.
        const conflict = response.status === 409
        this.messages.add({ key: 'preoffer-review', severity: conflict ? 'warn' : 'error', summary: conflict ? 'Ya no espera tu decisión' : 'No se pudo guardar', detail: response.msg, life: 6000 })
        if (conflict) this.load()
      } else {
        this.returning = false
        this.messages.add({
          key: 'preoffer-review',
          severity: 'success',
          summary: body.decision === 'authorized' ? 'Preoferta autorizada' : 'Preoferta regresada',
          detail: body.decision === 'authorized' ? 'El correo sale en el siguiente minuto.' : 'El cliente la ve «En revisión».',
          life: 4000,
        })
        this.api.refresh()
        this.load()
      }
      this.cd.markForCheck()
    })
  }
}
